import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  DollarSign,
  Wallet,
  TrendingUp,
  Activity,
  HardHat,
  Wrench,
} from 'lucide-react';
import { formatUsd, formatPct, marginTone } from '@/lib/jobCostingData';

const Stat = ({ label, value, caption, Icon, tone = 'default', testId }) => {
  const accent = {
    default: 'text-[hsl(var(--primary))]',
    success: 'text-[hsl(var(--success))]',
    warning: 'text-[hsl(var(--warning))]',
    danger: 'text-[hsl(var(--destructive))]',
  }[tone];
  const bar = {
    default: 'bg-[hsl(var(--primary))]/60',
    success: 'bg-[hsl(var(--success))]/60',
    warning: 'bg-[hsl(var(--warning))]/60',
    danger: 'bg-[hsl(var(--destructive))]/60',
  }[tone];
  return (
    <Card
      className="relative overflow-hidden border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid={testId}
    >
      <span aria-hidden className={cn('metallic absolute left-0 top-0 h-full w-[2px]', bar)} />
      {/* Hierarchy flip (Job Tracker walkthrough, Sep 5): the LABEL leads —
          big enough to read first — and the number sits under it at a calmer
          size. Before this, the value was 3xl over a 10px label and the page
          read as "big numbers, guess what they mean". */}
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <span className={cn('text-[11px] leading-snug sm:text-sm lg:text-xs 2xl:text-sm font-bold uppercase tracking-[0.12em] break-words', accent)}>
            {label}
          </span>
          {Icon ? <Icon size={16} className={cn('opacity-80', accent)} /> : null}
        </div>
        <div
          className="mt-1.5 text-lg sm:text-xl font-semibold tracking-tight text-foreground tabular-nums"
          data-testid={`${testId}-value`}
        >
          {value}
        </div>
        {caption ? (
          <div className="mt-1 text-xs text-muted-foreground">{caption}</div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export const JobCostingStatsBar = ({ stats }) => {
  const { t } = useTranslation();
  const marginTonee = marginTone(stats?.marginPct);
  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5"
      data-testid="costing-stats"
    >
      <Stat
        label={t('jobCosting.stats.revenue')}
        value={formatUsd(stats?.revenue || 0)}
        caption={t('jobCosting.stats.revenueCaption', { count: stats?.count || 0 })}
        Icon={DollarSign}
        testId="costing-stat-revenue"
      />
      <Stat
        label={t('jobCosting.stats.actualCost')}
        value={formatUsd(stats?.actualCost || 0)}
        caption={t('jobCosting.stats.actualCaption', {
          labor: formatUsd(stats?.laborCost || 0),
          materials: formatUsd(stats?.materialsCost || 0),
        })}
        Icon={Wallet}
        tone="warning"
        testId="costing-stat-actual"
      />
      <Stat
        label={t('jobCosting.stats.grossProfit')}
        value={formatUsd(stats?.grossProfit || 0)}
        caption={t('jobCosting.stats.grossCaption', { amount: formatUsd(stats?.budget || 0) })}
        Icon={TrendingUp}
        tone={marginTonee === 'danger' ? 'danger' : 'success'}
        testId="costing-stat-profit"
      />
      <Stat
        label={t('jobCosting.stats.margin')}
        value={formatPct(stats?.marginPct || 0)}
        caption={t('jobCosting.stats.marginCaption', {
          healthy: stats?.healthy || 0,
          watch: stats?.watch || 0,
          risk: stats?.risk || 0,
        })}
        Icon={Activity}
        tone={marginTonee}
        testId="costing-stat-margin"
      />
    </div>
  );
};

export const InlineLaborMaterialsSplit = ({ labor, materials }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-md border border-border bg-[hsl(var(--muted))]/40 px-3 py-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <HardHat size={11} />
          {t('jobCosting.detail.laborRow')}
        </div>
        <div className="mt-1 text-sm font-semibold text-foreground tabular-nums">
          {formatUsd(labor?.cost || 0)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {(Number(labor?.hours) || 0).toFixed(1)}h
        </div>
      </div>
      <div className="rounded-md border border-border bg-[hsl(var(--muted))]/40 px-3 py-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <Wrench size={11} />
          {t('jobCosting.detail.materialsRow')}
        </div>
        <div className="mt-1 text-sm font-semibold text-foreground tabular-nums">
          {formatUsd(materials?.total || 0)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {(materials?.entries || []).length}
        </div>
      </div>
    </div>
  );
};

export default JobCostingStatsBar;
